import { useCallback, useEffect, useRef, useState } from 'react';
import notify from '../../../shared/feedback/notifications';
import subDays from 'date-fns/subDays';
import { Translate } from '../../../shared/LanguageService';
import ZentuvoInvoicingApi, { IProcountorInvoicePreviewResponse } from '../ZentuvoInvoicingApi';
import { getDefaultInvoiceDateSelection, getRangeForInvoiceDate } from '../invoiceDateSelection';
import { dateToApiValue, getErrorMessage } from '../procountorInvoiceHelpers';

const today = new Date();

type UseProcountorPreviewArgs = {
    onLoaded: (response: IProcountorInvoicePreviewResponse, preserveSelection: boolean) => void;
    onCleared: () => void;
};

type UseProcountorPreviewResult = {
    fromDate: Date | null;
    toDate: Date | null;
    directInvoices: boolean;
    invoiceDates: string[];
    selectedInvoiceDate: string;
    invoiceDateSelected: boolean;
    preview: IProcountorInvoicePreviewResponse | null;
    loadingPreview: boolean;
    isPreviewStale: boolean;
    setFromDate: (value: Date | null) => void;
    setToDate: (value: Date | null) => void;
    setDirectInvoices: (value: boolean) => void;
    markStale: () => void;
    handleInvoiceDateChange: (value: string) => void;
    loadPreview: (preserveSelection: boolean) => Promise<IProcountorInvoicePreviewResponse | null>;
};

export function useProcountorPreview({ onLoaded, onCleared }: UseProcountorPreviewArgs): UseProcountorPreviewResult {
    const latestRequestIdRef = useRef(0);
    const [preview, setPreview] = useState<IProcountorInvoicePreviewResponse | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [toDate, setToDate] = useState<Date | null>(today);
    const [fromDate, setFromDate] = useState<Date | null>(subDays(today, 17));

    const [invoiceDates, setInvoiceDates] = useState<string[]>([]);
    const [selectedInvoiceDate, setSelectedInvoiceDate] = useState<string>('');
    const invoiceDateSelected = selectedInvoiceDate !== '';

    const [directInvoices, setDirectInvoices] = useState(false);
    const [isPreviewStale, setIsPreviewStale] = useState(true);

    const markStale = useCallback(() => setIsPreviewStale(true), []);

    const loadPreview = useCallback(
        (preserveSelection: boolean) => {
            if (!fromDate || !toDate) {
                setPreview(null);
                onCleared();
                return Promise.resolve(null);
            }

            const requestId = latestRequestIdRef.current + 1;
            latestRequestIdRef.current = requestId;
            setLoadingPreview(true);

            const previewRequestLabel = `zentuvo-preview-${requestId}`;
            console.time(previewRequestLabel);

            return ZentuvoInvoicingApi.getPreview(dateToApiValue(fromDate), dateToApiValue(toDate), directInvoices)
                .then((response) => {
                    if (requestId !== latestRequestIdRef.current) {
                        console.timeEnd(previewRequestLabel);
                        return null;
                    }

                    setPreview(response);
                    setIsPreviewStale(false);
                    onLoaded(response, preserveSelection);

                    return response;
                })
                .catch((error) => {
                    if (requestId !== latestRequestIdRef.current) {
                        console.timeEnd(previewRequestLabel);
                        return null;
                    }

                    setPreview(null);
                    onCleared();
                    notify.failure(getErrorMessage(error, Translate('zentuvo-invoicing/error/no-results-found')));
                    console.error('zentuvoInvoicing preview error', error);
                    return null;
                })
                .finally(() => {
                    console.timeEnd(previewRequestLabel);
                    if (requestId === latestRequestIdRef.current) {
                        setLoadingPreview(false);
                    }
                });
        },
        [fromDate, toDate, directInvoices, onLoaded, onCleared]
    );

    useEffect(() => {
        ZentuvoInvoicingApi.getInvoiceDates()
            .then((dates) => {
                setInvoiceDates(dates);

                const defaultSelection = getDefaultInvoiceDateSelection(dates);
                if (defaultSelection) {
                    setSelectedInvoiceDate(defaultSelection.selectedInvoiceDate);
                    setFromDate(defaultSelection.from);
                    setToDate(defaultSelection.to);
                    setIsPreviewStale(true);
                }
            })
            .catch((error) => {
                console.error('zentuvoInvoicing invoice-dates error', error);
            });
    }, []);

    const handleInvoiceDateChange = useCallback((value: string) => {
        setSelectedInvoiceDate(value);
        setIsPreviewStale(true);

        if (value) {
            const range = getRangeForInvoiceDate(value);
            setFromDate(range.from);
            setToDate(range.to);
        }
    }, []);

    return {
        fromDate,
        toDate,
        directInvoices,
        invoiceDates,
        selectedInvoiceDate,
        invoiceDateSelected,
        preview,
        loadingPreview,
        isPreviewStale,
        setFromDate,
        setToDate,
        setDirectInvoices,
        markStale,
        handleInvoiceDateChange,
        loadPreview
    };
}
